import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { DateCalendar, PickersDay, type PickersDayProps } from '@mui/x-date-pickers';
import {
  addDays,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
  type Locale,
} from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import {
  availabilityService,
  type AvailabilitySlot,
} from '../../shared/services/availability.service';

// ── Day view (whole-hour chips) ─────────────────────────────────────────
const DAY_VIEW_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];
const DAY_VIEW_DURATION_MIN = 60;
// Long enough to highlight which days have availability on the calendar
// without paginating per month.
const DAY_VIEW_WINDOW_DAYS = 90;

// ── Week view (30-min grid) ─────────────────────────────────────────────
const WEEK_VIEW_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];
const WEEK_VIEW_MINUTES = [0, 30];
const WEEK_VIEW_DURATION_MIN = 30;

type ViewMode = 'day' | 'week';
type CellKey = string; // "yyyy-MM-ddTHH:mm" — matches the slot's local start
type CellState = 'empty' | 'free' | 'booked';
const ALL_FILTERS: CellState[] = ['empty', 'free', 'booked'];

function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function cellKey(date: Date, time: string): CellKey {
  return `${dayKey(date)}T${time}`;
}

function weekTimeOptions(): string[] {
  return WEEK_VIEW_HOURS.flatMap((h) =>
    WEEK_VIEW_MINUTES.map(
      (m) => `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
    ),
  );
}

function localIsoFromCell(date: Date, time: string): string {
  return new Date(`${dayKey(date)}T${time}:00`).toISOString();
}

function localIsoFromHour(date: Date, hour: number): string {
  const d = new Date(date);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

export function ProviderCalendarPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const { notify } = useNotification();
  const { translate } = useApiError();
  const qc = useQueryClient();

  const today = startOfDay(new Date());
  const [view, setView] = useState<ViewMode>('day');

  // Day view state
  const [selectedDate, setSelectedDate] = useState<Date>(today);

  // Week view state
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(today, { weekStartsOn: 1 }));
  const [filters, setFilters] = useState<CellState[]>(ALL_FILTERS);

  // Each view fetches the range it needs. React-query keeps both cached.
  const dayFromIso = today.toISOString();
  const dayToIso = addDays(today, DAY_VIEW_WINDOW_DAYS).toISOString();
  const dayQuery = useQuery({
    queryKey: ['availability', 'me', 'day', dayFromIso, dayToIso],
    queryFn: () => availabilityService.listMine(dayFromIso, dayToIso),
    enabled: view === 'day',
  });

  const weekFromIso = weekStart.toISOString();
  const weekToIso = addDays(weekStart, 7).toISOString();
  const weekQuery = useQuery({
    queryKey: ['availability', 'me', 'week', weekFromIso, weekToIso],
    queryFn: () => availabilityService.listMine(weekFromIso, weekToIso),
    enabled: view === 'week',
  });

  // Day-view derivations
  const dayAllSlots = useMemo(() => dayQuery.data?.data ?? [], [dayQuery.data?.data]);
  const daysWithSlots = useMemo(() => {
    const out = new Set<string>();
    for (const s of dayAllSlots) out.add(dayKey(new Date(s.startsAt)));
    return out;
  }, [dayAllSlots]);
  const slotByHour = useMemo<Record<number, AvailabilitySlot>>(() => {
    const out: Record<number, AvailabilitySlot> = {};
    for (const s of dayAllSlots) {
      const d = new Date(s.startsAt);
      if (!isSameDay(d, selectedDate)) continue;
      // Only show slots that align with whole-hour chips.
      if (d.getMinutes() === 0) out[d.getHours()] = s;
    }
    return out;
  }, [dayAllSlots, selectedDate]);

  // Week-view derivations
  const weekAllSlots = useMemo(() => weekQuery.data?.data ?? [], [weekQuery.data?.data]);
  const slotByCell = useMemo<Record<CellKey, AvailabilitySlot>>(() => {
    const out: Record<CellKey, AvailabilitySlot> = {};
    for (const s of weekAllSlots) {
      const d = new Date(s.startsAt);
      out[cellKey(d, format(d, 'HH:mm'))] = s;
    }
    return out;
  }, [weekAllSlots]);

  // Mutations — invalidate both queries so the other view stays consistent.
  const invalidateAll = () => qc.invalidateQueries({ queryKey: ['availability', 'me'] });

  const createHour = useMutation({
    mutationFn: ({ date, hour }: { date: Date; hour: number }) =>
      availabilityService.createMany([
        {
          startsAt: localIsoFromHour(date, hour),
          durationMinutes: DAY_VIEW_DURATION_MIN,
        },
      ]),
    onSuccess: invalidateAll,
    onError: (err) => notify.error(translate(err)),
  });

  const createCell = useMutation({
    mutationFn: ({ date, time }: { date: Date; time: string }) =>
      availabilityService.createMany([
        {
          startsAt: localIsoFromCell(date, time),
          durationMinutes: WEEK_VIEW_DURATION_MIN,
        },
      ]),
    onSuccess: invalidateAll,
    onError: (err) => notify.error(translate(err)),
  });

  const removeOne = useMutation({
    mutationFn: (slotId: string) => availabilityService.delete(slotId),
    onSuccess: invalidateAll,
    onError: (err) => notify.error(translate(err)),
  });

  const handleHourClick = (hour: number) => {
    const slot = slotByHour[hour];
    if (slot?.booked) return;
    if (slot) removeOne.mutate(slot.id);
    else createHour.mutate({ date: selectedDate, hour });
  };

  const handleCellClick = (date: Date, time: string, slot?: AvailabilitySlot) => {
    if (slot?.booked) return;
    if (slot) removeOne.mutate(slot.id);
    else createCell.mutate({ date, time });
  };

  const handleFilterChange = (_e: React.MouseEvent<HTMLElement>, next: CellState[]) => {
    setFilters(next.length === 0 ? ALL_FILTERS : next);
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const times = weekTimeOptions();
  const canGoPrev = !isBefore(weekStart, today);

  const formattedSelectedDate = format(selectedDate, 'EEEE d MMMM yyyy', {
    locale: dateLocale,
  });

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('availability.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('availability.subtitle')}
      </Typography>

      <Tabs
        value={view}
        onChange={(_, v) => v && setView(v)}
        sx={{
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab value="day" label={t('availability.viewDay')} />
        <Tab value="week" label={t('availability.viewWeek')} />
      </Tabs>

      {view === 'day' ? (
        <DayViewBody
          isLoading={dayQuery.isLoading}
          selectedDate={selectedDate}
          onSelectDate={(d) => setSelectedDate(d)}
          today={today}
          daysWithSlots={daysWithSlots}
          slotByHour={slotByHour}
          onHourClick={handleHourClick}
          formattedSelectedDate={formattedSelectedDate}
          t={t}
        />
      ) : (
        <WeekViewBody
          isLoading={weekQuery.isLoading}
          weekStart={weekStart}
          onPrev={() => setWeekStart((w) => addDays(w, -7))}
          onNext={() => setWeekStart((w) => addDays(w, 7))}
          onThisWeek={() => setWeekStart(startOfWeek(today, { weekStartsOn: 1 }))}
          canGoPrev={canGoPrev}
          today={today}
          days={days}
          times={times}
          slotByCell={slotByCell}
          filters={filters}
          onFilterChange={handleFilterChange}
          onCellClick={handleCellClick}
          dateLocale={dateLocale}
          t={t}
        />
      )}
    </Container>
  );
}

// ── Day view ───────────────────────────────────────────────────────────

function DayViewBody({
  isLoading,
  selectedDate,
  onSelectDate,
  today,
  daysWithSlots,
  slotByHour,
  onHourClick,
  formattedSelectedDate,
  t,
}: {
  isLoading: boolean;
  selectedDate: Date;
  onSelectDate: (d: Date) => void;
  today: Date;
  daysWithSlots: Set<string>;
  slotByHour: Record<number, AvailabilitySlot>;
  onHourClick: (hour: number) => void;
  formattedSelectedDate: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Grid container spacing={3}>
      {/* Calendar */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            p: { xs: 1, sm: 2 },
          }}
        >
          <DateCalendar
            value={selectedDate}
            onChange={(d) => d && onSelectDate(startOfDay(d))}
            disablePast
            maxDate={addDays(today, DAY_VIEW_WINDOW_DAYS - 1)}
            slots={{
              day: (props: PickersDayProps<Date>) => (
                <DayWithDot {...props} hasSlots={daysWithSlots.has(dayKey(props.day))} />
              ),
            }}
          />
        </Box>
      </Grid>

      {/* Hour chips */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            p: { xs: 2, sm: 2.5 },
            height: '100%',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, textTransform: 'capitalize', mb: 0.5 }}
          >
            {t('availability.hoursForDay', { date: formattedSelectedDate })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('availability.addHourHint')}
          </Typography>

          <Grid container spacing={1}>
            {DAY_VIEW_HOURS.map((hour) => {
              const slot = slotByHour[hour];
              const isBooked = Boolean(slot?.booked);
              const isFree = Boolean(slot && !slot.booked);
              return (
                <Grid item xs={4} sm={3} key={hour}>
                  <HourChip
                    label={`${hour.toString().padStart(2, '0')}:00`}
                    isFree={isFree}
                    isBooked={isBooked}
                    onClick={() => onHourClick(hour)}
                  />
                </Grid>
              );
            })}
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('availability.bookedHint')}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

// ── Week view ──────────────────────────────────────────────────────────

function WeekViewBody({
  isLoading,
  weekStart,
  onPrev,
  onNext,
  onThisWeek,
  canGoPrev,
  today,
  days,
  times,
  slotByCell,
  filters,
  onFilterChange,
  onCellClick,
  dateLocale,
  t,
}: {
  isLoading: boolean;
  weekStart: Date;
  onPrev: () => void;
  onNext: () => void;
  onThisWeek: () => void;
  canGoPrev: boolean;
  today: Date;
  days: Date[];
  times: string[];
  slotByCell: Record<CellKey, AvailabilitySlot>;
  filters: CellState[];
  onFilterChange: (e: React.MouseEvent<HTMLElement>, next: CellState[]) => void;
  onCellClick: (date: Date, time: string, slot?: AvailabilitySlot) => void;
  dateLocale: Locale | undefined;
  t: (k: string) => string;
}) {
  return (
    <>
      {/* Week navigator */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={onPrev} disabled={!canGoPrev} aria-label={t('booking.previousWeek')}>
          <ChevronLeftRoundedIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 600, flex: 1, textTransform: 'capitalize' }}>
          {format(weekStart, 'd MMM', { locale: dateLocale })}
          {' – '}
          {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: dateLocale })}
        </Typography>
        <Button onClick={onThisWeek}>{t('availability.thisWeek')}</Button>
        <IconButton onClick={onNext} aria-label={t('booking.nextWeek')}>
          <ChevronRightRoundedIcon />
        </IconButton>
      </Stack>

      {/* Filter chips — click "Booked" alone for a booked-only view. */}
      <ToggleButtonGroup
        value={filters}
        onChange={onFilterChange}
        size="small"
        aria-label={t('availability.filterAria')}
        sx={{
          mb: 2,
          flexWrap: 'wrap',
          '& .MuiToggleButton-root': {
            border: 'none',
            px: 1.25,
            py: 0.5,
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
            '&.Mui-selected': {
              bgcolor: 'transparent',
              color: 'text.primary',
              fontWeight: 600,
            },
            '&.Mui-selected:hover': { bgcolor: 'action.hover' },
          },
        }}
      >
        <ToggleButton value="empty">
          <FilterDot kind="empty" />
          {t('availability.legendEmpty')}
        </ToggleButton>
        <ToggleButton value="free">
          <FilterDot kind="free" />
          {t('availability.legendFree')}
        </ToggleButton>
        <ToggleButton value="booked">
          <FilterDot kind="booked" />
          {t('availability.legendBooked')}
        </ToggleButton>
      </ToggleButtonGroup>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Grid container columns={7} sx={{ bgcolor: 'action.hover' }}>
            {days.map((d) => (
              <Grid
                item
                xs={1}
                key={d.toISOString()}
                sx={{
                  py: 1,
                  textAlign: 'center',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderRight: 'none' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    textTransform: 'capitalize',
                    fontWeight: 600,
                  }}
                >
                  {format(d, 'EEEE', { locale: dateLocale })}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {format(d, 'd MMM', { locale: dateLocale })}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Slot cells */}
          {times.map((time) => (
            <Grid container columns={7} key={time}>
              {days.map((d) => {
                const isPast = isBefore(d, today);
                const slot = slotByCell[cellKey(d, time)];
                const state: CellState = slot ? (slot.booked ? 'booked' : 'free') : 'empty';
                const visible = filters.includes(state);
                return (
                  <Grid
                    item
                    xs={1}
                    key={`${d.toISOString()}-${time}`}
                    sx={{
                      borderTop: '1px solid',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      '&:last-of-type': { borderRight: 'none' },
                    }}
                  >
                    <SlotCell
                      time={time}
                      isPast={isPast}
                      state={state}
                      visible={visible}
                      onClick={() => !isPast && onCellClick(d, time, slot)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
}

// ── Local helpers ──────────────────────────────────────────────────────

function DayWithDot(props: PickersDayProps<Date> & { hasSlots: boolean }) {
  const { hasSlots, ...rest } = props;
  return (
    <Box sx={{ position: 'relative' }}>
      <PickersDay {...rest} />
      {hasSlots && !rest.outsideCurrentMonth && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: 'success.main',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

function HourChip({
  label,
  isFree,
  isBooked,
  onClick,
}: {
  label: string;
  isFree: boolean;
  isBooked: boolean;
  onClick: () => void;
}) {
  const disabled = isBooked;
  return (
    <Button
      fullWidth
      variant={isFree ? 'contained' : 'outlined'}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      startIcon={
        isBooked ? (
          <LockRoundedIcon sx={{ fontSize: '1rem' }} />
        ) : isFree ? (
          <CheckRoundedIcon sx={{ fontSize: '1rem' }} />
        ) : (
          <AddRoundedIcon sx={{ fontSize: '1rem' }} />
        )
      }
      sx={{
        py: 1,
        fontWeight: 600,
        borderColor: 'divider',
        color: isFree ? 'common.white' : isBooked ? 'text.disabled' : 'text.primary',
        bgcolor: isFree
          ? 'success.main'
          : isBooked
            ? 'action.disabledBackground'
            : 'background.paper',
        '&:hover': isFree
          ? { bgcolor: 'success.dark' }
          : { bgcolor: 'action.hover', borderColor: 'primary.main' },
        '&.Mui-disabled': {
          bgcolor: 'action.disabledBackground',
          color: 'text.disabled',
        },
      }}
    >
      {label}
    </Button>
  );
}

function SlotCell({
  time,
  isPast,
  state,
  visible,
  onClick,
}: {
  time: string;
  isPast: boolean;
  state: CellState;
  visible: boolean;
  onClick: () => void;
}) {
  // Filtered-out cells render an empty placeholder of matching height so the
  // grid layout stays stable when the user toggles filters.
  if (!visible) {
    return <Box sx={{ minHeight: 44 }} aria-hidden />;
  }
  const isFree = state === 'free';
  const isBooked = state === 'booked';
  const disabled = isPast || isBooked;
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      aria-disabled={disabled}
      sx={{
        minHeight: 44,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        bgcolor: isBooked
          ? 'action.disabledBackground'
          : isFree
            ? (theme) =>
                theme.palette.mode === 'dark'
                  ? `${theme.palette.success.main}33`
                  : `${theme.palette.success.main}1F`
            : 'transparent',
        color: isBooked ? 'text.disabled' : isFree ? 'success.dark' : 'text.secondary',
        fontWeight: isFree || isBooked ? 600 : 400,
        fontSize: '0.875rem',
        transition: 'background-color 120ms ease',
        '&:hover': disabled
          ? {}
          : {
              bgcolor: isFree
                ? (theme) =>
                    theme.palette.mode === 'dark'
                      ? `${theme.palette.error.main}33`
                      : `${theme.palette.error.main}1F`
                : (theme) =>
                    theme.palette.mode === 'dark'
                      ? `${theme.palette.primary.main}1F`
                      : `${theme.palette.primary.main}14`,
            },
      }}
    >
      {isBooked && <LockRoundedIcon sx={{ fontSize: '0.95rem' }} />}
      {time}
    </Box>
  );
}

function FilterDot({ kind }: { kind: CellState }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        mr: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor:
          kind === 'free'
            ? (theme) => `${theme.palette.success.main}66`
            : kind === 'booked'
              ? 'action.disabledBackground'
              : 'transparent',
      }}
    />
  );
}
